import React, { useState, useEffect } from "react";
import "tailwindcss/tailwind.css";
import { useNavigate } from "react-router-dom";

const RegistrationForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    username: "",
    password: "",
    repassword: "",
  });

  const [errors, setErrors] = useState({
    password: "",
    repassword: "",
    email: "",
  });

  const [serverMessage, setServerMessage] = useState(""); // To store server response
  const [serverMessageType, setServerMessageType] = useState(""); // To track if message is error or success
  const [isLoading, setIsLoading] = useState(false); // To track button loading state

  const allowedDomains = ["gmail.com", "yahoo.com", "yandex.com", "outlook.com"];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      validatePassword(value);
    }

    if (name === "repassword") {
      validateConfirmPassword(value, formData.password);
    }

    if (name === "username") {
      validateEmail(value);
    }
  };

  const validatePassword = (password) => {
    const passwordErrors = [];
    if (password.length < 8) passwordErrors.push("Must be at least 8 characters.");
    if (!/[A-Z]/.test(password)) passwordErrors.push("Must include an uppercase letter.");
    if (!/[a-z]/.test(password)) passwordErrors.push("Must include a lowercase letter.");
    if (!/[0-9]/.test(password)) passwordErrors.push("Must include a number.");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) passwordErrors.push("Must include a special character.");

    setErrors((prevErrors) => ({
      ...prevErrors,
      password: passwordErrors.join(" "),
    }));
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      repassword: confirmPassword !== password ? "Passwords do not match." : "",
    }));
  };

  const validateEmail = (email) => {
    const domain = email.split("@")[1];
    if (!domain || !allowedDomains.includes(domain)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email domain must be one of: Gmail, Yahoo, Yandex, or Outlook.",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!errors.password && !errors.repassword && !errors.email) {
      setIsLoading(true); // Start spinner
      try {
        const response = await fetch("/api/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          setServerMessage("Registration successful! Please verify your email to activate your account.");
          setServerMessageType("success");
        } else {
          const errorData = await response.json();
          setServerMessage(errorData.error || "Registration failed.");
          setServerMessageType("error");
        }
      } catch (error) {
        setServerMessage("Server error: " + error.message);
        setServerMessageType("error");
      } finally {
        setIsLoading(false); // Stop spinner
      }
    }
  };

  useEffect(() => {
    if (serverMessageType === "success") {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [serverMessageType, navigate]);

  return (
    <section className="min-h-screen bg-white md:bg-slate-100">
      <main className="flex items-center justify-center px-8 py-8 sm:px-12 lg:col-span-7 lg:px-16 lg:py-12 xl:col-span-6">
        <div className="max-w-lg md:bg-slate-50 px-8 py-12 mx-auto lg:max-w-3xl md:shadow-sm md:rounded-md">
          <a href="/">
            <img src="./image/prep2.png" width="260" className="mx-auto py-6" alt="Logo" />
          </a>
          <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="FirstName" className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <input
                type="text"
                id="FirstName"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                className="mt-1 w-full py-3 rounded-md border-gray-200 bg-white text-sm text-gray-700 shadow-sm"
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="LastName" className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <input
                type="text"
                id="LastName"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                className="mt-1 w-full py-3 rounded-md border-gray-200 bg-white text-sm text-gray-700 shadow-sm"
              />
            </div>
            <div className="col-span-6">
              <label htmlFor="Email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="Email"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="mt-1 w-full py-3 rounded-md border-gray-200 bg-white text-sm text-gray-700 shadow-sm"
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="Password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                id="Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="mt-1 w-full py-3 rounded-md border-gray-200 bg-white text-sm text-gray-700 shadow-sm"
              />
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">{errors.password}</p>
              )}
              <ul className="list-disc text-gray-600 pl-6 mt-2 text-sm">
                <li>At least 8 characters long</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
                <li>One special character (e.g., @, $, #, etc.)</li>
              </ul>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="PasswordConfirmation" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                type="password"
                id="PasswordConfirmation"
                name="repassword"
                value={formData.repassword}
                onChange={handleInputChange}
                className="mt-1 w-full py-3 rounded-md border-gray-200 bg-white text-sm text-gray-700 shadow-sm"
              />
              {errors.repassword && (
                <p className="text-sm text-red-600 mt-1">{errors.repassword}</p>
              )}
            </div>
            <div className="col-span-6 sm:flex sm:items-center sm:gap-4">
              <button
                type="submit"
                className="inline-block shrink-0 rounded border border-gray-600 bg-gray-900 px-12 py-3 text-sm font-medium text-white transition hover:bg-transparent hover:text-black focus:outline-none focus:ring active:text-blue-500"
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white inline-block"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                ) : (
                  "Create an account"
                )}
              </button>
              <p className="mt-4 text-sm text-gray-500 sm:mt-0">
                Already have an account?{" "}
                <a href="/login" className="text-gray-700 underline">
                  Log in
                </a>
                .
              </p>
            </div>
          </form>
          {serverMessage && (
            <p className={`mt-4 text-sm ${serverMessageType === "error" ? "text-red-600" : "text-green-600"}`}>
              {serverMessage}
            </p>
          )}
        </div>
      </main>
    </section>
  );
};

export default RegistrationForm;
